import React, { useState, useEffect } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import { AnimatePresence } from 'framer-motion';
import { Toaster } from 'react-hot-toast';

// Context
import { AuthProvider, useAuth } from './contexts/AuthContext';
import { CompanyProvider } from './contexts/CompanyContext';
import { SocketProvider } from './contexts/SocketContext';

// Components
import LoadingSpinner from './components/common/LoadingSpinner';
import ProtectedRoute from './components/auth/ProtectedRoute';
import PublicRoute from './components/auth/PublicRoute';

// Pages
import Login from './pages/Login';
import Onboarding from './pages/Onboarding';
import Dashboard from './pages/Dashboard';
import SuperAdminDashboard from './pages/SuperAdminDashboard';
import CompanyAdminDashboard from './pages/CompanyAdminDashboard';
import UserDashboard from './pages/UserDashboard';
import LeadsPage from './pages/LeadsPage';
import CustomersPage from './pages/CustomersPage';
import FollowupsPage from './pages/FollowupsPage';
import ProfilePage from './pages/ProfilePage';
import Companies from './pages/Companies';
import SystemAnalytics from './pages/SystemAnalytics';
import Settings from './pages/Settings';
import Plans from './pages/Plans';
import Countries from './pages/Countries';
import Currencies from './pages/Currencies';
import Industries from './pages/Industries';
import LeadStages from './pages/LeadStages';
import UserManagement from './pages/UserManagement';
import TeamAnalytics from './pages/TeamAnalytics';
import CompanySettings from './pages/CompanySettings';
import UserAnalytics from './pages/UserAnalytics';
import PerformanceGoals from './pages/PerformanceGoals';
import OrphanedDataManager from './pages/OrphanedDataManager';
import WebhookIntegration from './pages/WebhookIntegration';
import TermsOfService from './pages/TermsOfService';
import PrivacyPolicy from './pages/PrivacyPolicy';
import RefundPolicy from './pages/RefundPolicy';
import NotFound from './pages/NotFound';

// Styles
import './App.css';

function AppContent() {
  const { loading } = useAuth();
  const [isReady, setIsReady] = useState(false);

  useEffect(() => {
    // Simulate app initialization
    const timer = setTimeout(() => {
      setIsReady(true);
    }, 1000);

    return () => clearTimeout(timer);
  }, []);

  if (loading || !isReady) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-blue-50 to-indigo-100">
        <LoadingSpinner size="large" />
      </div>
    );
  }

  return (
    <Router
      future={{
        v7_startTransition: true,
        v7_relativeSplatPath: true
      }}
    >
      <div className="App min-h-screen bg-gray-50">
        <AnimatePresence mode="wait">
          <Routes>
            {/* Public Routes */}
            <Route
              path="/login"
              element={
                <PublicRoute>
                  <Login />
                </PublicRoute>
              }
            />

            <Route
              path="/onboarding"
              element={
                <PublicRoute>
                  <Onboarding />
                </PublicRoute>
              }
            />

            <Route path="/terms" element={<TermsOfService />} />
            <Route path="/privacy" element={<PrivacyPolicy />} />
            <Route path="/refund" element={<RefundPolicy />} />

            {/* Protected Routes */}
            <Route
              path="/"
              element={
                <ProtectedRoute>
                  <Navigate to="/dashboard" replace />
                </ProtectedRoute>
              }
            />

            <Route
              path="/dashboard"
              element={
                <ProtectedRoute>
                  <Dashboard />
                </ProtectedRoute>
              }
            />

            {/* Super Admin Routes */}
            <Route
              path="/super-admin"
              element={
                <ProtectedRoute requiredRole="super_admin">
                  <SuperAdminDashboard />
                </ProtectedRoute>
              }
            />

            <Route
              path="/companies"
              element={
                <ProtectedRoute requiredRole="super_admin">
                  <Companies />
                </ProtectedRoute>
              }
            />

            <Route
              path="/orphaned-data"
              element={
                <ProtectedRoute requiredRole="super_admin">
                  <OrphanedDataManager />
                </ProtectedRoute>
              }
            />

            <Route
              path="/analytics"
              element={
                <ProtectedRoute requiredRole="super_admin">
                  <SystemAnalytics />
                </ProtectedRoute>
              }
            />

            <Route
              path="/settings"
              element={
                <ProtectedRoute requiredRole="super_admin">
                  <Settings />
                </ProtectedRoute>
              }
            />

            <Route
              path="/plans"
              element={
                <ProtectedRoute requiredRole="super_admin">
                  <Plans />
                </ProtectedRoute>
              }
            />

            <Route
              path="/countries"
              element={
                <ProtectedRoute requiredRole="super_admin">
                  <Countries />
                </ProtectedRoute>
              }
            />

            <Route
              path="/currencies"
              element={
                <ProtectedRoute requiredRole="super_admin">
                  <Currencies />
                </ProtectedRoute>
              }
            />

            <Route
              path="/industries"
              element={
                <ProtectedRoute requiredRole="super_admin">
                  <Industries />
                </ProtectedRoute>
              }
            />

            {/* Company Admin Routes */}
            <Route
              path="/company-admin"
              element={
                <ProtectedRoute requiredRole="company_admin">
                  <CompanyAdminDashboard />
                </ProtectedRoute>
              }
            />

            <Route
              path="/user-management"
              element={
                <ProtectedRoute requiredRole="company_admin">
                  <UserManagement />
                </ProtectedRoute>
              }
            />

            <Route
              path="/team-analytics"
              element={
                <ProtectedRoute requiredRole="company_admin">
                  <TeamAnalytics />
                </ProtectedRoute>
              }
            />

            <Route
              path="/company-settings"
              element={
                <ProtectedRoute requiredRole="company_admin">
                  <CompanySettings />
                </ProtectedRoute>
              }
            />

            <Route
              path="/lead-stages"
              element={
                <ProtectedRoute requiredRole="company_admin">
                  <LeadStages />
                </ProtectedRoute>
              }
            />

            <Route
              path="/performance-goals"
              element={
                <ProtectedRoute requiredRole="company_admin">
                  <PerformanceGoals />
                </ProtectedRoute>
              }
            />

            <Route
              path="/integrations"
              element={
                <ProtectedRoute requiredRole="company_admin">
                  <WebhookIntegration />
                </ProtectedRoute>
              }
            />

            {/* User Dashboard */}
            <Route
              path="/user-dashboard"
              element={
                <ProtectedRoute requiredRole="user">
                  <UserDashboard />
                </ProtectedRoute>
              }
            />

            {/* CRM Module Routes */}
            <Route
              path="/leads"
              element={
                <ProtectedRoute>
                  <LeadsPage />
                </ProtectedRoute>
              }
            />

            <Route
              path="/leads/:id"
              element={
                <ProtectedRoute>
                  <LeadsPage />
                </ProtectedRoute>
              }
            />

            <Route
              path="/customers"
              element={
                <ProtectedRoute>
                  <CustomersPage />
                </ProtectedRoute>
              }
            />

            <Route
              path="/customers/:id"
              element={
                <ProtectedRoute>
                  <CustomersPage />
                </ProtectedRoute>
              }
            />

            <Route
              path="/followups"
              element={
                <ProtectedRoute>
                  <FollowupsPage />
                </ProtectedRoute>
              }
            />

            <Route
              path="/user-analytics"
              element={
                <ProtectedRoute requiredRole="user">
                  <UserAnalytics />
                </ProtectedRoute>
              }
            />

            {/* Profile Route */}
            <Route
              path="/profile"
              element={
                <ProtectedRoute>
                  <ProfilePage />
                </ProtectedRoute>
              }
            />

            {/* 404 Route */}
            <Route path="*" element={<NotFound />} />
          </Routes>
        </AnimatePresence>

        {/* Global Toast Notifications */}
        <Toaster
          position="top-right"
          toastOptions={{
            duration: 4000,
            style: {
              background: '#363636',
              color: '#fff',
            },
            success: {
              duration: 3000,
              iconTheme: {
                primary: '#10B981',
                secondary: '#fff',
              },
            },
            error: {
              duration: 5000,
              iconTheme: {
                primary: '#EF4444',
                secondary: '#fff',
              },
            },
          }}
        />
      </div>
    </Router>
  );
}

function App() {
  return (
    <AuthProvider>
      <CompanyProvider>
        <SocketProvider>
          <AppContent />
        </SocketProvider>
      </CompanyProvider>
    </AuthProvider>
  );
}

export default App;